import { CrossIcon } from '@synergy/theme';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, SxProps, Theme, useTheme } from '@mui/material';
import { ReactNode, useState } from 'react';
import { makeStyles } from '@mui/styles';

import { PoolTypography } from '../typography/pool-typography';

// The dialog's proportions come from the theme, so the backoffice keeps its own
// look while PALO gets the prototype's compact one without touching call sites.
const useStyles = makeStyles((theme: Theme) => ({
    closeContainer: {
        position: 'absolute',
        top: theme.modal.closeOffset,
        right: theme.modal.closeOffset,
        zIndex: 1,
        '& svg': {
            width: theme.modal.closeSize,
            height: theme.modal.closeSize,
            color: theme.modal.closeColor,
        },
    },
    title: {
        textAlign: theme.modal.titleAlign,
        padding: theme.modal.titlePadding,
        borderBottom: theme.modal.titleBorderBottom,
        '& span': {
            fontSize: theme.modal.titleFontSize,
            fontWeight: theme.modal.titleFontWeight,
        },
    },
    titleDivider: {
        boxShadow: '0px 2px 14px rgba(0, 0, 0, 0.26)',
        marginBottom: theme.spacing(),
    },
    actionButtonsCenter: {
        justifyContent: 'space-between',
    },
    actionButtonsRight: {
        justifyContent: 'right',
    },
    actionButtons: {
        padding: theme.modal.actionsPadding,
        borderTop: theme.modal.actionsBorderTop,
        gap: '8px',
        '& button': {
            textTransform: theme.modal.actionTextTransform,
            fontWeight: theme.modal.actionFontWeight,
            letterSpacing: theme.modal.actionLetterSpacing,
        },
    },
}));

interface PoolModalSx {
    content?: SxProps;
}

export interface PoolModalProps {
    isVisible: boolean;
    title: ReactNode;
    titleComponent?: ReactNode | ReactNode[];
    handleClose?: () => void;
    actionChildren?: ReactNode | ReactNode[];
    actionChildrenCentered?: boolean;
    width?: 'sm' | 'lg';
    disableTitleDivider?: boolean;
    children?: ((hasTitleDivider: boolean) => JSX.Element) | ReactNode;
    sx?: PoolModalSx;
    hasModalTypography?: boolean;
}

export const PoolModal = (props: PoolModalProps) => {
    const classes = useStyles();
    const { title, titleComponent, width, isVisible, handleClose, sx, disableTitleDivider } = props;
    const [hasTitleDivider, setHasTitleDivider] = useState(false);

    const renderDialogActions = () => {
        const centered = props.actionChildrenCentered ?? true;
        const className = `${classes.actionButtons} ${
            centered ? classes.actionButtonsCenter : classes.actionButtonsRight
        }`;
        if (props.actionChildren) {
            return <DialogActions className={className}>{props.actionChildren}</DialogActions>;
        } else {
            return null;
        }
    };

    const { modal } = useTheme();
    const titleStyle = `${classes.title} ${hasTitleDivider && !disableTitleDivider ? classes.titleDivider : ''}`;

    // Reads from the theme like the rest of the dialog does. These three were
    // written in by hand, which is why a message dialog kept its old centred
    // look while its frame had already moved to PALO.
    const sxContent: SxProps = {
        ...sx?.content,
        ...(props.hasModalTypography && {
            fontSize: modal.messageFontSize,
            lineHeight: modal.messageLineHeight,
            whiteSpace: 'pre-line',
            textAlign: modal.messageAlign,
        }),
    };

    return (
        <Dialog
            open={isVisible}
            onClose={handleClose}
            fullWidth={true}
            maxWidth={width ?? 'sm'}
            PaperProps={{
                id: 'dialog',
                sx: (theme: Theme) => ({
                    borderRadius: theme.modal.paperBorderRadius,
                    boxShadow: theme.modal.paperBoxShadow,
                }),
            }}
        >
            <DialogTitle className={titleStyle}>
                <PoolTypography typography="h4" component="span">
                    {title}
                </PoolTypography>
                {titleComponent}
            </DialogTitle>
            {handleClose && (
                <IconButton className={classes.closeContainer} onClick={handleClose}>
                    <CrossIcon />
                </IconButton>
            )}
            <DialogContent
                ref={(ref: HTMLDivElement) => setHasTitleDivider((ref?.clientHeight ?? 0) < (ref?.scrollHeight ?? 0))}
                sx={[(theme: Theme) => ({ padding: theme.modal.contentPadding }), sxContent] as SxProps<Theme>}
            >
                {typeof props.children === 'function' ? props.children(hasTitleDivider) : props.children}
            </DialogContent>
            {renderDialogActions()}
        </Dialog>
    );
};
